import { Component, Input, NgZone } from '@angular/core';
import { take } from 'rxjs/operators';

import { Region } from '../../../../models/content';
import { ModalService } from '../../../../services/modal.service';
import { SuburbSubRegion } from '../../../../services/suburb-areas.service';
import { colourSlot } from '../workspace/map-colours';
import { ContentRecordsService } from '../workspace/content-records.service';
import { WorkspaceStateService } from '../workspace/workspace-state.service';

/**
 * A region's sub-regions in Website → Regions (#280; Ivan 2026-11-02: regions and sub-regions are managed in the
 * Regions window). Each with its colour on the map, its suburbs, whether its page is on the website; recolour, delete
 * (empty and off the website), and "New sub-region". A sub-region's page and its suburbs are edited in the Suburbs tool,
 * which opens over this window on it: the page editor and the map need that tool's services. Records are written as the
 * Suburbs tool writes them (ContentRecordsService, its own instance here).
 */
@Component({
  selector: 'app-region-subregions',
  templateUrl: './region-subregions.component.html',
  styleUrls: ['./region-subregions.component.scss'],
  providers: [WorkspaceStateService, ContentRecordsService],
})
export class RegionSubRegionsComponent {
  @Input() region!: Region;
  /** This region's sub-regions, in the server's order (suburb-areas meta). */
  @Input() subRegions: SuburbSubRegion[] = [];
  /**
   * Something here changed: the window reloads its list. A function from the window, not an output: a write here
   * updates the account, the window can redraw this row, and a callback still reaches the window from this instance
   * when its Suburbs tool closes later (an output of a destroyed instance reaches nothing).
   */
  @Input() reload: () => void = () => undefined;

  /** The sub-region whose colour picker is open. */
  colourFor: string | null = null;
  busy = false;

  constructor(
    private records: ContentRecordsService,
    private modalService: ModalService,
    private zone: NgZone
  ) {}

  /** Its colour slot (1–8): its own, else the next in its region's list, as on the map. */
  slot(sr: SuburbSubRegion, i: number): number {
    return colourSlot(sr.mapColour, i);
  }

  /** Why it can't be deleted now, or null when it can: no suburbs in it, not on the website. */
  deleteBlock(sr: SuburbSubRegion): string | null {
    if (sr.members) return `It has ${sr.members} suburb${sr.members === 1 ? '' : 's'} in it: take them out first.`;
    if (sr.published) return "It's on the website: un-publish its page first.";
    return null;
  }

  pickColour(sr: SuburbSubRegion, slot: number): void {
    this.colourFor = null;
    const record = this.records.recordById(sr.id);
    if (!record || record.mapColour === slot) return;
    this.busy = true;
    this.records.writeFresh(
      record,
      (fresh) => ({ ...fresh, mapColour: slot }),
      () => this.done()
    );
  }

  delete(sr: SuburbSubRegion): void {
    const record = this.records.recordById(sr.id);
    if (!record || this.deleteBlock(sr)) return;
    const designed = this.records.designedPage(record);
    this.records.removeWithPage(
      record,
      designed?.published !== true ? designed : undefined,
      {
        title: 'Delete sub-region',
        copy: `Delete the sub-region "${record.name}" and its page? It has no suburbs and isn't on the website, so nothing there changes.`,
        label: 'Delete',
      },
      () => this.done()
    );
  }

  /** Its page, in the Suburbs tool's editor (opened over this window). */
  editPage(sr: SuburbSubRegion): void {
    this.openSuburbs({ subRegion: sr.id, editPage: true });
  }

  /** Its suburbs, on the Suburbs map: its panel, with its members and Fill from council areas. */
  openOnMap(sr: SuburbSubRegion): void {
    this.openSuburbs({ subRegion: sr.id });
  }

  newSubRegion(): void {
    this.openSuburbs({ newSubRegion: this.region._meta.id });
  }

  private openSuburbs(data: { subRegion?: string; editPage?: boolean; newSubRegion?: string }): void {
    // The tool loads on demand: the dialog is there once its chunk is.
    void this.modalService.listSuburbs(data).then((ref) =>
      ref?.onClose.pipe(take(1)).subscribe(() => this.zone.run(() => this.reload()))
    );
  }

  private done(): void {
    this.busy = false;
    this.reload();
  }
}
